import { cn } from 'noddi-ui-common';
import { addDays } from 'noddi-util';
import { Tabs as TabsPrimitive } from 'radix-ui';
import { NoddiDatePicker } from '../../Overlay/Date/NoddiDatePicker';
import { NoddiIconButton } from '../NoddiIconButton';
import { useDatePickerQueryParam } from './useDatePickerQueryParam';

interface Props {
  defaultValue?: Date;
  label?: string;
  tooltip?: string;
  className?: string;
  showPrevNextDayArrows?: boolean;
  showQuickDateShortcuts?: boolean;
  /** Fired whenever the user picks a new date (arrow, calendar, or shortcut). Useful for resetting dependent filters. */
  onDateChange?: (date: Date | undefined) => void;
}

export const DatePickerQueryParam = ({
  defaultValue,
  label,
  tooltip,
  className,
  showPrevNextDayArrows = true,
  showQuickDateShortcuts = true,
  onDateChange
}: Props) => {
  const { date, setDate } = useDatePickerQueryParam({ defaultValue });
  const selectedStamp = new Date(date).setHours(0, 0, 0, 0);

  const handleDateChange = (next: Date | undefined) => {
    setDate(next);
    onDateChange?.(next);
  };

  const picker = (
    <NoddiDatePicker
      className={cn('min-w-[189px]', className)}
      date={date}
      label={label}
      tooltip={tooltip}
      onDateChange={handleDateChange}
    />
  );

  const dateRow = showPrevNextDayArrows ? (
    <div className='flex items-center gap-2'>
      <NoddiIconButton iconName='ChevronLeft' variant='outline' onClick={() => handleDateChange(addDays(date, -1))} />
      {picker}
      <NoddiIconButton iconName='ChevronRight' variant='outline' onClick={() => handleDateChange(addDays(date, 1))} />
    </div>
  ) : (
    picker
  );

  if (!showQuickDateShortcuts) {
    return dateRow;
  }

  const activeOffset = QUICK_JUMPS.find(({ offset }) => dayFromOffset(offset).getTime() === selectedStamp)?.offset;

  return (
    <div className='flex flex-col items-end gap-2'>
      {dateRow}
      <TabsPrimitive.Root
        value={activeOffset?.toString() ?? ''}
        onValueChange={(value) => handleDateChange(dayFromOffset(parseInt(value)))}
      >
        <TabsPrimitive.List className='flex flex-wrap justify-end gap-1'>
          {QUICK_JUMPS.map(({ label, offset }) => {
            const isActive = offset === activeOffset;
            return (
              <TabsPrimitive.Trigger
                key={label}
                value={offset.toString()}
                className={cn(
                  'rounded-md border px-2.5 py-1 text-xs outline-hidden transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background hover:bg-muted'
                )}
              >
                {label}
              </TabsPrimitive.Trigger>
            );
          })}
        </TabsPrimitive.List>
      </TabsPrimitive.Root>
    </div>
  );
};

const QUICK_JUMPS: { label: string; offset: number }[] = [
  { label: 'Today', offset: 0 },
  { label: 'Tomorrow', offset: 1 },
  { label: 'In 2 days', offset: 2 },
  { label: 'In 3 days', offset: 3 }
];

const dayFromOffset = (offset: number) => {
  const next = new Date();
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + offset);
  return next;
};
